import './form-number-field.scss';

import { Box, Grid, TextField, TextFieldProps } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

import { ConvergeInputLabel } from './converge-input-label';
import { FC } from 'react';
import { FormFieldContainer } from './form-field-container';
import { NewConvergeInputLabel } from './new-converge-input-label';

export interface IFormNumberFieldProps
  extends Pick<
    TextFieldProps,
    'label' | 'required' | 'InputProps' | 'name' | 'disabled' | 'size' | 'value'
  > {
  name: string;
  containerWidth?: number;
  thousandSeparator?: string;
  decimalScale?: number;
  isAllowed?: (values: NumberFormatValues) => boolean;
  onBlur?: () => void;
  isSameRow?: boolean;
  startNumber?: number;
  isPrimaryEndorsement?: boolean;
  isAutomaticEndorsement?: boolean;
  isConsentToSettleEndorsementNewAcquiredEntity?: boolean;
  placeholder?: string;
}

export const FormNumberField: FC<IFormNumberFieldProps> = ({
  name,
  label,
  required,
  containerWidth = 6,
  decimalScale,
  isAllowed,
  isSameRow,
  onBlur: onBlurProp,
  startNumber,
  isPrimaryEndorsement = false,
  isAutomaticEndorsement = false,
  placeholder,
  isConsentToSettleEndorsementNewAcquiredEntity = false,
  ...otherProps
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, onBlur, value, name },
        fieldState: { error },
      }) => (
        <>
          {!isSameRow ? (
            <FormFieldContainer md={containerWidth}>
              <NewConvergeInputLabel
                label={label}
                required={required}
                className={error?.message ? 'error-message' : ''}
              />

              <NumericFormat
                name={name}
                onBlur={() => {
                  if (onBlur) {
                    onBlur();
                  }

                  if (onBlurProp) {
                    onBlurProp();
                  }
                }}
                customInput={TextField}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontWeight: 600,
                    fontSize: '14px',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: '2px solid #23a892',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: '2px solid #23a892',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #4d4d4d',
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #bfbdbd',
                    },
                    '& input::placeholder': {
                      opacity: 1,
                      fontWeight: 600,
                      color: '#999999',
                    },
                  },
                }}
                variant="outlined"
                placeholder={placeholder}
                fullWidth
                error={!!error}
                size="small"
                value={isNaN(value) ? value : Number(value)}
                onValueChange={(v) => {
                  onChange(v.value);
                }}
                className="form-number-background"
                isAllowed={isAllowed}
                decimalScale={decimalScale}
                helperText={
                  error ? (
                    <Box
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      {error.message as string}
                    </Box>
                  ) : (
                    ''
                  )
                }
                {...otherProps}
              />
            </FormFieldContainer>
          ) : (
            <FormFieldContainer
              md={containerWidth}
              display="flex"
              justify-content="space-around"
            >
              <Grid item md={isPrimaryEndorsement ? 2 : 7} paddingRight={'10%'}>
                <ConvergeInputLabel label={label} required={required} />
              </Grid>
              <Grid item md={isPrimaryEndorsement ? 2 : 5}>
                <NumericFormat
                  name={name}
                  onBlur={() => {
                    if (onBlur) {
                      onBlur();
                    }

                    if (onBlurProp) {
                      onBlurProp();
                    }
                  }}
                  customInput={TextField}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontWeight: 600,
                      fontSize: '14px',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid #23a892',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid #23a892',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #4d4d4d',
                      },
                      '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #bfbdbd',
                      },
                      '& input::placeholder': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#999999',
                      },
                    },
                  }}
                  variant="outlined"
                  fullWidth
                  error={
                    !!error ||
                    ((value < 1 || !value) && isAutomaticEndorsement) ||
                    ((value < 1 || value > 100 || !value) &&
                      isConsentToSettleEndorsementNewAcquiredEntity)
                  }
                  helperText={
                    error
                      ? (error?.message as unknown as string)
                      : (value < 1 || !value) && isAutomaticEndorsement
                      ? 'Please enter 1 or more days'
                      : (value < 1 || value > 100 || !value) &&
                        isConsentToSettleEndorsementNewAcquiredEntity
                      ? 'Please enter 1-100 %'
                      : ''
                  }
                  size="small"
                  value={isNaN(value) ? value : Number(value)}
                  onValueChange={(v) => {
                    onChange(v.value);
                  }}
                  className="form-number-background"
                  isAllowed={isAllowed}
                  decimalScale={decimalScale}
                  {...otherProps}
                />
              </Grid>
            </FormFieldContainer>
          )}
        </>
      )}
    />
  );
};
